// ---------------------------------------------- modules import
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FirebaseReducer,
  isEmpty,
  isLoaded,
  useFirestoreConnect
} from "react-redux-firebase";
import { useHistory } from "react-router-dom";

import firebase from "../../../config/fbInit";
import { AppState } from "../../../store/rootReducer";
import { IUserData, IOrderedAlbum } from "../../../store/firebase/common";
import PermissionHelper, {
  MenuIndex
} from "../../../store/helpers/permissionHelper";
import ValidationHelper, {
  pattern
} from "../../../store/helpers/validationHelper";
import { IAlbum, newAlbum } from "../../../store/album/common";
import {
  thunkAddAlbum,
  thunkUpdateAlbum,
  thunkDeleteAlbum
} from "../../../store/album/thunkActions";

// ---------------------------------------------- reduce ordered data
const reduceAlbums = (orderedAlbums: IOrderedAlbum[]) =>
  orderedAlbums.reduce(
    (currentAlbums, orderedAlbum) => [...currentAlbums, { ...orderedAlbum }],
    [] as IAlbum[]
  );

// ---------------------------------------------- custom hooks
export const useAuth = () => {
  // ---------------------------------------------- redux connect
  const auth = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).auth
  );
  const profile = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).profile
  );

  // ---------------------------------------------- local state
  const [areLoaded, setAreLoaded] = useState(
    isLoaded(auth) && isLoaded(profile)
  );
  const [isEmptied, setIsEmptied] = useState(isEmpty(auth) || isEmpty(profile));

  // ---------------------------------------------- effects
  useEffect(() => {
    setAreLoaded(isLoaded(auth) && isLoaded(profile));
    setIsEmptied(isEmpty(auth) || isEmpty(profile));
  }, [auth, profile]);

  return {
    auth,
    profile,
    areLoaded,
    isEmptied
  };
};

export const useAlbums = (index: MenuIndex) => {
  const collectionPath = PermissionHelper.getCollection(index);
  const reducerPath = PermissionHelper.getReducer(index);
  const dispatch = useDispatch();

  // ---------------------------------------------- firestore connect
  useFirestoreConnect({
    collection: collectionPath,
    orderBy: ["lastModified", "desc"]
  });

  // ---------------------------------------------- redux connect
  const orderedAlbums = useSelector(
    (state: AppState) =>
      state.firestore.ordered[collectionPath] as IOrderedAlbum[]
  );
  const loading = useSelector((state: AppState) => state[reducerPath].loading);
  const error = useSelector((state: AppState) => state[reducerPath].error);

  // ---------------------------------------------- local state
  const [albums, setAlbums] = useState<IAlbum[]>([]);
  const [filtered, setFiltered] = useState<IAlbum[]>([]);
  const [fetching, setFetching] = useState(true);
  const [criteria, setCriteria] = useState({
    prop: "name",
    keywords: ""
  });

  // ---------------------------------------------- handlers
  const handleFilter = (criterion: string, value: string | null) =>
    setCriteria({
      ...criteria,
      [criterion]: value
    });

  const handleDelete = (auth: FirebaseReducer.AuthState, id: string) => {
    if (auth.uid) dispatch(thunkDeleteAlbum(id, collectionPath));
  };

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedAlbums)) {
      if (!isEmpty(orderedAlbums)) {
        const currentAlbums = reduceAlbums(orderedAlbums);

        setAlbums(currentAlbums);
      } else {
        setAlbums([]);
      }

      setFetching(false);
    } else {
      setFetching(true);
    }
  }, [orderedAlbums]);

  useEffect(() => {
    const { prop, keywords } = criteria;
    let filtered = albums;

    setFiltered(
      filtered.filter(album =>
        RegExp(`${keywords}`, "i").test(
          prop === "creator" ? album[prop].name : album[prop]
        )
      )
    );
  }, [criteria, albums]);

  // ---------------------------------------------- return value
  return {
    loading,
    error,
    filtered,
    fetching,
    criteria,
    onFilter: handleFilter,
    onDelete: handleDelete
  };
};

export const useAlbum = (index: MenuIndex, id?: string) => {
  const collectionPath = PermissionHelper.getCollection(index);
  const dispatch = useDispatch();

  // ---------------------------------------------- router state
  let history = useHistory();

  // ---------------------------------------------- firestore connect
  useFirestoreConnect({
    collection: collectionPath,
    orderBy: ["lastModified", "desc"]
  });

  // ---------------------------------------------- redux connect
  const orderedAlbums = useSelector(
    (state: AppState) =>
      state.firestore.ordered[collectionPath] as IOrderedAlbum[]
  );

  // ---------------------------------------------- local state
  const [album, setAlbum] = useState<IAlbum>(newAlbum());

  const isAlbumValid = () =>
    ValidationHelper.validate(album.name, pattern.album.name);

  // ---------------------------------------------- handlers
  const handleChange = (prop: string, value: string) =>
    setAlbum({
      ...album,
      [prop]: value
    });

  const handleAdd = (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (auth.uid && PermissionHelper.getModify(profile.permissions, index)) {
      dispatch(
        thunkAddAlbum(
          {
            ...album,
            createdAt: firebase.firestore.Timestamp.now(),
            creator: {
              email: profile.email,
              name: profile.name
            },
            lastModified: firebase.firestore.Timestamp.now()
          },
          collectionPath
        )
      );
      history.push(path);
    }
  };

  const handleUpdate = (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (auth.uid && PermissionHelper.getModify(profile.permissions, index)) {
      dispatch(
        thunkUpdateAlbum(
          {
            ...album,
            lastModified: firebase.firestore.Timestamp.now()
          },
          collectionPath
        )
      );
      history.push(path);
    }
  };

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedAlbums) && !isEmpty(orderedAlbums)) {
      const currentAlbums = reduceAlbums(orderedAlbums);

      const currentAlbum = currentAlbums.find(
        currentAlbum => currentAlbum.id === id
      );

      if (currentAlbum) setAlbum(currentAlbum);
    }
  }, [orderedAlbums, id]);

  // ---------------------------------------------- return value
  return {
    album,
    isAlbumValid,
    onChange: handleChange,
    onAdd: handleAdd,
    onUpdate: handleUpdate
  };
};
